/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Minimal toast system — a context and a corner stack. No external deps.
 *
 * Ported from MoveHelper. It exists because "Copied" and "Joined household"
 * are the kind of confirmation that must not steal focus or need dismissing:
 * an alert() for a clipboard write is worse than no feedback at all.
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

type Tone = "ok" | "danger" | "neutral";

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
}

const Ctx = createContext<(message: string, tone?: Tone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const push = useCallback((message: string, tone: Tone = "neutral") => {
    // Date.now() alone collides when two fire in the same millisecond, which
    // duplicate-keys the list and makes React drop one.
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-5 right-5 z-[60] flex flex-col gap-2"
           role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id}
               className={`animate-fade-in rounded-lg border bg-paper px-4 py-2.5 text-sm text-ink shadow-soft ${
                 t.tone === "ok" ? "border-ok/30"
                 : t.tone === "danger" ? "border-danger/30"
                 : "border-rule"}`}>
            <span className="mr-2" aria-hidden>
              {t.tone === "ok" ? "✓" : t.tone === "danger" ? "⚠" : "•"}
            </span>
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  return useContext(Ctx);
}
